import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { api, priceLabel, whatsappLink } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActive(0);
    api
      .get(`/products/${id}`)
      .then(({ data }) => {
        setProduct(data);
        api.get("/products", { params: { category: data.category } }).then(({ data: list }) => {
          setRelated(list.filter((p) => p.id !== data.id).slice(0, 3));
        });
      })
      .catch(() => setNotFound(true));
  }, [id]);

  const photos = useMemo(() => {
    if (!product) return [];
    return [...new Set([product.image, ...(product.images || [])].filter(Boolean))];
  }, [product]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6" data-testid="product-not-found">
        <p className="font-serif text-4xl italic text-ivory">Pezzo non trovato.</p>
        <Link to="/catalogo?categoria=orologi" className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-gold">
          Torna alla collezione
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="product-loading">
        <p className="font-serif text-2xl italic text-muted">Caricamento…</p>
      </div>
    );
  }

  const specs = [
    ["Referenza", product.reference],
    ["Anno", product.year || "—"],
    ["Condizione", product.condition],
    ["Disponibilità", product.stock > 0 ? "Disponibile" : "Venduto"],
  ];

  return (
    <div className="px-5 pb-24 pt-28 sm:px-6 lg:px-12 lg:pb-32 lg:pt-32" data-testid="product-detail-page">
      <Link
        to={`/catalogo?categoria=${product.category === "gioielli" ? "gioielli" : "orologi"}`}
        data-testid="back-to-catalog"
        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-muted transition-colors duration-500 hover:text-gold"
      >
        <ArrowLeft size={14} /> Torna alla collezione
      </Link>

      <div className="mt-10 grid gap-10 lg:mt-12 lg:grid-cols-2 lg:gap-16">
        <div className="min-w-0 lg:sticky lg:top-28 lg:self-start">
          <Reveal y={24}>
            <div className="spotlight relative overflow-hidden bg-elevated">
              <AnimatePresence mode="wait">
                <motion.img
                  key={photos[active]}
                  src={photos[active]}
                  alt={`${product.name} — foto ${active + 1}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.45 }}
                  className="w-full object-cover"
                  data-testid="gallery-main-image"
                />
              </AnimatePresence>
              {photos.length > 1 && (
                <p className="absolute bottom-4 right-5 z-10 font-mono text-[10px] tracking-[0.3em] text-ivory/70">
                  {active + 1} / {photos.length}
                </p>
              )}
            </div>
            {photos.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-5 sm:overflow-visible sm:pb-0" data-testid="gallery-thumbnails">
                {photos.map((src, i) => (
                  <button
                    key={src}
                    onClick={() => setActive(i)}
                    data-testid={`gallery-thumb-${i}`}
                    aria-label={`Foto ${i + 1}`}
                    className={`h-20 w-20 shrink-0 overflow-hidden border transition-colors duration-500 sm:h-auto sm:w-auto ${
                      i === active ? "border-gold" : "border-line opacity-50 hover:opacity-90"
                    }`}
                  >
                    <img src={src} alt="" className="aspect-square w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </Reveal>
        </div>

        <div>
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-gold">
              {product.category === "orologi" ? "Orologio" : product.category === "vintage" ? "Orologio Vintage" : "Gioiello"}
              {product.year ? ` · ${product.year}` : ""}
            </p>
            <h1 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-ivory lg:text-6xl" data-testid="product-name">
              {product.name}
            </h1>
            <p className="mt-6 font-mono text-3xl text-gold" data-testid="product-price">
              {priceLabel(product)}
            </p>
            <p className="mt-8 max-w-lg text-base font-light leading-relaxed text-muted" data-testid="product-description">
              {product.description}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <dl className="mt-12 border-t border-line">
              {specs.map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between border-b border-line py-4">
                  <dt className="font-mono text-[11px] uppercase tracking-[0.25em] text-muted">{label}</dt>
                  <dd className="font-mono text-sm text-ivory">{value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>

          <Reveal delay={0.15}>
            <a
              href={whatsappLink(product)}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-cta-button"
              className="mt-12 flex w-full items-center justify-center gap-3 border border-gold px-8 py-5 font-mono text-xs uppercase tracking-[0.3em] text-gold transition-colors duration-500 hover:bg-gold hover:text-void"
            >
              <MessageCircle size={16} strokeWidth={1.5} /> Contattaci su WhatsApp per informazioni
            </a>
            <p className="mt-6 text-[11px] leading-relaxed text-muted">
              Orologi certificati e garantiti 12 mesi. Spedizione assicurata in tutto il mondo. Per maggiori dettagli:
              +39 351 199 7843 · carminelucariello@yahoo.it
            </p>
          </Reveal>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-32" data-testid="related-products">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-gold">Potrebbe piacerti</p>
          </Reveal>
          <div className="mt-10 grid gap-8 sm:grid-cols-3">
            {related.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <Link to={`/prodotto/${p.id}`} data-testid={`related-product-${p.reference}`} className="group block">
                  <div className="spotlight relative overflow-hidden bg-elevated">
                    <img
                      src={p.image}
                      alt={p.name}
                      className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${
                        p.category === "orologi" ? "aspect-square sm:aspect-[4/5]" : "aspect-[4/5]"
                      }`}
                    />
                  </div>
                  <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-gold">{p.reference}</p>
                  <p className="mt-1 font-serif text-xl text-ivory">{p.name}</p>
                  <p className="mt-1 font-mono text-sm text-gold">{priceLabel(p)}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
